import { NavLink } from "react-router-dom";
import {
  LayoutDashboard, Users, BookOpen, GraduationCap, ClipboardList,
  FileText, BarChart3, Settings as SettingsIcon, PenSquare, Award,
  Building2, Layers, ChevronsLeft, Printer, LifeBuoy, IdCard, Trophy, PieChart,
  ArrowUpRight, Archive as ArchiveIcon,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const superAdminItems = [
  { section: "المنصّة" },
  { to: "/schools", icon: Building2, label: "إدارة المدارس", testId: "nav-schools" },
  { to: "/support", icon: LifeBuoy, label: "تذاكر الدعم", testId: "nav-support-super" },
];

const tenantItems = [
  { to: "/dashboard", icon: LayoutDashboard, label: "لوحة القيادة", testId: "nav-dashboard" },
  { section: "الشؤون الأكاديمية" },
  { to: "/students", icon: Users, label: "الطلاب", testId: "nav-students" },
  { to: "/students/seat-numbers", icon: IdCard, label: "إدارة أرقام الجلوس", testId: "nav-seat-numbers-manage" },
  { to: "/subjects", icon: BookOpen, label: "المواد الدراسية", testId: "nav-subjects" },
  { to: "/templates", icon: ClipboardList, label: "قوالب التقييم", testId: "nav-templates" },
  { section: "بداية العام الدراسي" },
  { to: "/promotion", icon: ArrowUpRight, label: "ترحيل الطلاب لعام جديد", testId: "nav-promotion" },
  { section: "رصد الدرجات" },
  { to: "/grades/year-work", icon: PenSquare, label: "أعمال السنة", testId: "nav-year-work" },
  { to: "/grades/final-exam", icon: GraduationCap, label: "الاختبار النهائي", testId: "nav-final-exam" },
  { section: "التقارير والشهادات" },
  { to: "/certificates", icon: Award, label: "الشهادات", testId: "nav-certificates" },
  { to: "/reports", icon: FileText, label: "مراجعة الرصد", testId: "nav-reports" },
  { to: "/analysis", icon: BarChart3, label: "التحليل الإحصائي", testId: "nav-analysis" },
  { to: "/archive", icon: ArchiveIcon, label: "الأرشيف", testId: "nav-archive" },
  { section: "الطباعة" },
  { to: "/print/class-lists", icon: Printer, label: "قوائم الفصول", testId: "nav-class-lists" },
  { to: "/print/school-stats", icon: PieChart, label: "إحصائية المدرسة", testId: "nav-school-stats" },
  { to: "/print/top-students", icon: Trophy, label: "العشرة الأوائل", testId: "nav-top-students" },
  { to: "/print/seat-numbers", icon: IdCard, label: "أرقام الجلوس", testId: "nav-seat-numbers" },
  { to: "/print/grade-sheets", icon: Printer, label: "كشوف الرصد", testId: "nav-grade-sheets" },
  { section: "النظام" },
  { to: "/settings", icon: SettingsIcon, label: "إعدادات المدرسة", testId: "nav-settings" },
  { to: "/support", icon: LifeBuoy, label: "الدعم الفني", testId: "nav-support" },
];

export default function Sidebar() {
  const { user } = useAuth();
  const isSuper = user?.role === "super_admin";
  const items = isSuper ? superAdminItems : tenantItems;

  return (
    <aside
      className="w-[268px] shrink-0 flex flex-col sticky top-0 h-screen bg-white"
      style={{ borderInlineStart: "1px solid #E4E9E5" }}
      data-testid="app-sidebar"
    >
      {/* Brand */}
      <div className="px-5 py-5" style={{ borderBottom: "1px solid #EEF3EF" }}>
        <div className="flex items-center gap-3">
          <div
            className="h-11 w-11 rounded-xl flex items-center justify-center shrink-0"
            style={{
              background: "linear-gradient(135deg, #0E7C6F 0%, #14B8A6 100%)",
              boxShadow: "0 4px 12px -3px rgba(14, 124, 111, 0.35)",
            }}
          >
            <Layers className="h-5 w-5 text-white" strokeWidth={2} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="font-display text-[15.5px] font-bold text-slate-900 leading-tight tracking-tight truncate">
              المسار المصري
            </div>
            <div className="text-[10.5px] text-slate-500 mt-0.5 truncate">
              {isSuper ? "منصّة الإدارة" : "نظام إدارة الدرجات"}
            </div>
          </div>
        </div>
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto py-3 px-3">
        {items.map((item, i) =>
          item.section ? (
            <div
              key={`s-${i}`}
              className="px-3 pt-5 pb-2 first:pt-1 text-[10.5px] font-bold text-slate-400 tracking-[0.14em] uppercase"
            >
              {item.section}
            </div>
          ) : (
            <NavLink
              key={item.to}
              to={item.to}
              end
              data-testid={item.testId}
              className={({ isActive }) =>
                [
                  "group flex items-center gap-3 px-3 py-2.5 mb-1 text-[13.5px] rounded-xl relative",
                  "transition-all duration-150",
                  isActive
                    ? "text-white font-semibold"
                    : "text-slate-600 hover:bg-slate-50 hover:text-slate-900",
                ].join(" ")
              }
              style={({ isActive }) => isActive ? {
                background: "linear-gradient(135deg, #0E7C6F 0%, #0A5C52 100%)",
                boxShadow: "0 4px 12px -3px rgba(14, 124, 111, 0.35)",
              } : undefined}
            >
              {({ isActive }) => (
                <>
                  <item.icon
                    className={
                      "h-[18px] w-[18px] shrink-0 transition-colors " +
                      (isActive ? "text-white" : "text-slate-400 group-hover:text-[#0E7C6F]")
                    }
                    strokeWidth={1.75}
                  />
                  <span className="flex-1 truncate">{item.label}</span>
                  {isActive && (
                    <ChevronsLeft className="h-3.5 w-3.5 text-white/60" strokeWidth={2} />
                  )}
                </>
              )}
            </NavLink>
          )
        )}
      </nav>

      {/* Footer */}
      <div className="p-4" style={{ borderTop: "1px solid #EEF3EF" }}>
        <div
          className="rounded-xl p-3.5"
          style={{
            background: "linear-gradient(135deg, #F0FAF7 0%, #FEF9E7 100%)",
            border: "1px solid rgba(14, 124, 111, 0.12)",
          }}
        >
          <div className="flex items-center gap-2 mb-1.5">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.6)]" />
            <span className="text-[11px] font-bold text-slate-800">النظام يعمل</span>
          </div>
          <div className="text-[10.5px] text-slate-500 leading-relaxed">
            الإصدار 1.3 · متعدد المدارس
          </div>
        </div>
      </div>
    </aside>
  );
}
